import type {
  ConfigResponse,
  SendMessageResponse,
  StartSessionResponse,
  SubmitLeadResponse
} from './types';

export interface BootOptions {
  siteKey: string;
  apiBase: string;
  anonId?: string;
}

export class ChatApi {
  constructor(private readonly opts: BootOptions) {}

  private url(path: string) {
    const base = this.opts.apiBase.replace(/\/+$/, '');
    return `${base}/api/public-chat${path}`;
  }

  private headers(): Record<string, string> {
    const h: Record<string, string> = {
      'content-type': 'application/json',
      'x-chat-site': this.opts.siteKey,
      'x-correlation-id': randomCorrelationId()
    };
    if (this.opts.anonId) h['x-chat-anon'] = this.opts.anonId;
    return h;
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const res = await fetch(this.url(path), {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify(body),
      credentials: 'omit',
      mode: 'cors'
    });
    if (res.status === 429) throw new ApiError(429, 'rate_limited');
    if (!res.ok) throw new ApiError(res.status, await safeBody(res));
    return res.status === 204 ? (undefined as unknown as T) : ((await res.json()) as T);
  }

  async getConfig(): Promise<ConfigResponse> {
    const res = await fetch(
      `${this.url('/config')}?siteKey=${encodeURIComponent(this.opts.siteKey)}`,
      { mode: 'cors', credentials: 'omit', headers: this.headers() }
    );
    if (!res.ok) throw new ApiError(res.status, await safeBody(res));
    return (await res.json()) as ConfigResponse;
  }

  startSession(payload: {
    pageUrl?: string;
    pageTitle?: string;
    referrer?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
    anonId?: string;
  }): Promise<StartSessionResponse> {
    return this.post<StartSessionResponse>('/session', {
      siteKey: this.opts.siteKey,
      ...payload
    });
  }

  sendMessage(payload: {
    sessionId: string;
    body: string;
    clientMessageId?: string;
    quickActionId?: number;
  }): Promise<SendMessageResponse> {
    return this.post<SendMessageResponse>('/message', payload);
  }

  submitLead(payload: {
    sessionId: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    company?: string;
    role?: string;
    notes?: string;
  }): Promise<SubmitLeadResponse> {
    return this.post<SubmitLeadResponse>('/lead', payload);
  }

  logEvent(payload: {
    sessionId?: string;
    eventType: string;
    payload?: unknown;
  }): Promise<void> {
    return this.post<void>('/event', {
      siteKey: this.opts.siteKey,
      ...payload
    }).catch(() => undefined);
  }
}

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`chat api ${status}: ${body}`);
  }
}

async function safeBody(r: Response): Promise<string> {
  try { return await r.text(); } catch { return ''; }
}

function randomCorrelationId(): string {
  try { return crypto.randomUUID().replace(/-/g, ''); }
  catch { return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`; }
}
